import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { InferredValue, ConfidenceLegend } from "@/components/ConfidenceBadge";
import SubscribeCTA from "@/components/SubscribeCTA";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Search, FileText, FileSpreadsheet, Loader2, ChevronLeft, ChevronRight, Filter, X, ExternalLink, Lock } from "lucide-react";

function downloadBlob(data, filename) {
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const selectCls = "bg-[#111726] border border-[#27334D] rounded-lg text-sm px-3 py-2 focus:border-blue-500 focus:outline-none text-slate-200";

export default function Contacts() {
  const { user } = useAuth();
  const isPaid = user?.is_paid;
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState({ sectors: [], seniorities: [], regions: [] });
  const [filters, setFilters] = useState({ sector: "", seniority: "", region: "", search: "" });
  const [exporting, setExporting] = useState("");
  const [showCTA, setShowCTA] = useState(false);
  const pageSize = 25;

  useEffect(() => { api.get("/filters/options").then(({ data }) => setOptions(data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/contacts", { params });
      setContacts(data.contacts); setTotal(data.total);
    } catch {} finally { setLoading(false); }
  }, [page, filters]);

  useEffect(() => { load(); }, [load]);

  const setFilter = (k, v) => { setPage(1); setFilters((f) => ({ ...f, [k]: v })); };
  const clearFilters = () => { setPage(1); setFilters({ sector: "", seniority: "", region: "", search: "" }); };
  const hasFilters = Object.values(filters).some(Boolean);

  const doExport = async (fmt) => {
    if (!isPaid) { setShowCTA(true); return; }
    setExporting(fmt);
    try {
      const activeFilters = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const { data } = await api.post(`/export/${fmt}`, { filters: activeFilters, include_reasoning: true }, { responseType: "blob" });
      downloadBlob(data, fmt === "pdf" ? "linkviewer-report.pdf" : "linkviewer-network.xlsx");
      toast.success(`${fmt.toUpperCase()} exported`);
    } catch { toast.error("Export failed"); } finally { setExporting(""); }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <AppShell>
      <div className="animate-fade-up space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight">Contacts</h1>
            <p className="text-slate-500 text-sm mt-1 font-mono-niq">{total.toLocaleString()} enriched · inferred fields tagged by confidence</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => doExport("pdf")} disabled={exporting} data-testid="export-pdf-button" className="flex items-center gap-2 text-sm border border-[#27334D] hover:border-slate-500 px-4 py-2.5 rounded-lg transition-colors disabled:opacity-50">
              {!isPaid ? <Lock className="w-4 h-4 text-slate-500" /> : exporting === "pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4 text-red-400" />} PDF
            </button>
            <button onClick={() => doExport("excel")} disabled={exporting} data-testid="export-excel-button" className="flex items-center gap-2 text-sm border border-[#27334D] hover:border-slate-500 px-4 py-2.5 rounded-lg transition-colors disabled:opacity-50">
              {!isPaid ? <Lock className="w-4 h-4 text-slate-500" /> : exporting === "excel" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 text-emerald-400" />} Excel
            </button>
          </div>
        </div>

        {showCTA && !isPaid && (
          <SubscribeCTA title="Downloads are a Pro feature" subtitle="Free accounts are view-only. Go Pro to export PDF and Excel reports and unlock unlimited searches." />
        )}

        <div className="rounded-2xl border border-[#27334D] bg-[#111726] p-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="contacts-search-input" value={filters.search} onChange={(e) => setFilter("search", e.target.value)} placeholder="Search name, company, title…" className={`${selectCls} w-full pl-9`} />
          </div>
          <select data-testid="filter-sector-select" value={filters.sector} onChange={(e) => setFilter("sector", e.target.value)} className={selectCls}>
            <option value="">All sectors</option>
            {options.sectors.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select data-testid="filter-seniority-select" value={filters.seniority} onChange={(e) => setFilter("seniority", e.target.value)} className={selectCls}>
            <option value="">All seniority</option>
            {options.seniorities.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select data-testid="filter-region-select" value={filters.region} onChange={(e) => setFilter("region", e.target.value)} className={selectCls}>
            <option value="">All regions</option>
            {options.regions.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          {hasFilters && <button onClick={clearFilters} className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white px-2 py-2"><X className="w-3.5 h-3.5" /> Clear</button>}
        </div>

        <div className="rounded-xl border border-[#1E2A3F] bg-[#0D121C] px-4 py-2.5">
          <ConfidenceLegend />
        </div>

        <div className="rounded-2xl border border-[#27334D] bg-[#111726] overflow-hidden">
          {loading ? (
            <div className="py-20 flex justify-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : contacts.length === 0 ? (
            <div className="py-20 text-center text-slate-500">
              <Filter className="w-8 h-8 mx-auto mb-3 opacity-50" />
              <p>No contacts match. {!total && <Link to="/upload" className="text-blue-400">Import your connections →</Link>}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#1E2A3F] text-left">
                    {["Name", "Company", "Position", "Sector", "Seniority", "Region", "Email", "Connected"].map((h) => (
                      <th key={h} className="px-4 py-3 label-mono text-slate-500 font-normal whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((c) => (
                    <tr key={c.id} data-testid={`contact-row-${c.id}`} className="border-b border-[#1E2A3F] hover:bg-white/[0.02] transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap font-medium">
                        {c.profile_url ? (
                          <a href={c.profile_url} target="_blank" rel="noopener noreferrer" data-testid={`contact-linkedin-${c.id}`}
                            className="inline-flex items-center gap-1.5 hover:text-blue-400 transition-colors group">
                            {c.first_name} {c.last_name}
                            <ExternalLink className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </a>
                        ) : (
                          <span>{c.first_name} {c.last_name}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-slate-300">{c.normalized_company || c.company}</td>
                      <td className="px-4 py-3 text-slate-400 max-w-[220px] truncate" title={c.position}>{c.position}</td>
                      <td className="px-4 py-3"><InferredValue value={c.sector} level={c.sector_confidence} testId="confidence-tag-sector" /></td>
                      <td className="px-4 py-3"><InferredValue value={c.seniority} level={c.seniority_confidence} /></td>
                      <td className="px-4 py-3"><InferredValue value={c.region} level={c.region_confidence} /></td>
                      <td className="px-4 py-3 whitespace-nowrap text-slate-400 text-xs">{c.email ? <a href={`mailto:${c.email}`} data-testid={`contact-email-${c.id}`} className="hover:text-blue-400 transition-colors">{c.email}</a> : "—"}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-slate-500 font-mono-niq text-xs">{c.connected_on || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {total > pageSize && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-500 font-mono-niq">Page {page} of {totalPages}</span>
            <div className="flex gap-2">
              <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="p-2 rounded-lg border border-[#27334D] disabled:opacity-40 hover:border-slate-500 transition-colors"><ChevronLeft className="w-4 h-4" /></button>
              <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="p-2 rounded-lg border border-[#27334D] disabled:opacity-40 hover:border-slate-500 transition-colors"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
